import {RmCalendarEntryType, RmDayPart, RmDecisionMode, RmDecisionStatus} from '../../../enums/calendar-enums';
import {SearchModel} from '@yasc/yasc-material-components';
import {BaseTaskRes, ProjectMinRes} from '../crm';
import {Type} from 'class-transformer';
import {Salutation} from '../../../enums';

export class CalendarEntryBaseRes {
    id!: number;
    startUtc!: any;
    endUtc!: any;
    isAllDay!: boolean;
    dayPart!: RmDayPart;
    groupId!: string;
    ownerUserId!: number;
    createdByUserId!: string;
    absenceCreditHours!: number;
    absenceDays!: number;

    constructor(value?: Partial<CalendarEntryBaseRes>) {
        this.startUtc = undefined;
        this.endUtc = undefined;
        this.isAllDay = false;
        this.dayPart = RmDayPart.None as any;
    }
}

export class CalendarEntryRes extends CalendarEntryBaseRes {
    type!:RmCalendarEntryType

    title?: string;
    description?: string;
    color!: string;

    isActive!: boolean;

    decisionMode!: RmDecisionMode;
    decisionStatus!: RmDecisionStatus;

    taskId?: string;

    @Type(() => BaseTaskRes)
    task?: BaseTaskRes;

    projectId?: number;

    @Type(() => ProjectMinRes)
    project?: ProjectMinRes;

    @Type(() => CalendarDetailsUserRes)
    decidedByUser?: CalendarDetailsUserRes;
    decidedAtUtc?: any;

    creditedHours: number = 0;

    constructor(init?: Partial<CalendarEntryRes>) {
        // reasonable defaults (match backend defaults where possible)
        super(init)
        this.isActive = true;
        this.decisionMode = (RmDecisionMode as any).None ?? (0 as any);
        this.decisionStatus = (RmDecisionStatus as any).None ?? (0 as any);
        Object.assign(this, init);
    }

    get entryDurationInDays() {
        let duration = 0
        const current = new Date(this.startUtc);
        while (current <= new Date(this.endUtc)) {
            if(current.getDay() !== 0 || current.getDay() !== 6){
                duration++
            }
            current.setDate(current.getDate() + 1);
        }
        return duration;
    }
}

export class CalendarEntryCreateReq {
    start!: any;
    end!: any;

    isAllDay!: boolean;
    dayPart!: RmDayPart;

    carryOver!: boolean;
    overrideDurationMinutes?: number;


    title?: string;
    description?: string;
    color?: string;

    taskId?: string;
    projectId?: number;

    rRule?: string;
    recurrenceTimeZoneId?: string;

    constructor(init?: Partial<CalendarEntryCreateReq>) {
        this.isAllDay = false;
        this.dayPart = RmDayPart.None as any;
        this.carryOver = false;

        Object.assign(this, init);
    }
}


export class CalendarEntryUReq {
    id!: number;

    start?: any;
    end?: any;
    isAllDay?: boolean;
    dayPart?: RmDayPart;

    carryOver?: boolean;
    overrideDurationMinutes?: number;


    title?: string;
    description?: string;
    color?: string;

    taskId?: string;
    projectId?: number;

    rRule?: string;
    recurrenceTimeZoneId?: string;

    constructor(init?: Partial<CalendarEntryUReq>) {
        Object.assign(this, init);
    }
}

export class CalendarEntryCreateTypedReq extends CalendarEntryCreateReq {
    type!: RmCalendarEntryType;
    ownerUserId?: number;
    groupId?: string;
    participantUserIds?: number[];

    constructor(init?: Partial<CalendarEntryCreateTypedReq>) {
        super(init);
        Object.assign(this, init);
    }
}

export class CalendarEntryQueryReq {
    userIds?:number[]
    fromUtc?: string;
    toUtc?: string;
    types?: RmCalendarEntryType[];
    decisionStatuses?: RmDecisionStatus[];
    projectIds?: number[];
    onlyActive?: boolean;
    pageIndex?: number;
    pageSize?: number;

    constructor(init?: Partial<CalendarEntryQueryReq>) {
        Object.assign(this, init);
        // ensure defaults even if init provides null/undefined
        this.onlyActive = init?.onlyActive ?? true;
    }

}

export class CalendarMissingEntrySearchReq {
    userIds?: number[]
    excludeWeekends?: boolean;
    excludePublicHoliday?: boolean;
    excludeFutureDays?: boolean;
    fromUtc?: string;
    toUtc?: string;

    constructor(init?: Partial<CalendarEntryQueryReq>) {
        Object.assign(this, init);
    }
}

export class CalendarEntrySearchReq {
    ownerUserId?: number;
    fromUtc?: string;
    toUtc?: string;

    types?: RmCalendarEntryType[];
    decisionStatuses?: RmDecisionStatus[];

    onlyActive: boolean = true;
    search: SearchModel = new SearchModel({});

    constructor(init?: Partial<CalendarEntrySearchReq>) {
        Object.assign(this, init);
        // ensure defaults even if init provides null/undefined
        this.onlyActive = init?.onlyActive ?? true;
        this.search = init?.search ?? new SearchModel({});
    }
}

export class CalendarEntryCancelReq {
    applyToGroup: boolean = true;
    comment?: string;

    constructor(init?: Partial<CalendarEntryCancelReq>) {
        Object.assign(this, init);
        this.applyToGroup = init?.applyToGroup ?? true;
    }
}

export class CalendarInvitationRespondReq {
    entryId!: number;
    status!: RmDecisionStatus;
    comment?: string;

    constructor(init?: Partial<CalendarInvitationRespondReq>) {
        Object.assign(this, init);
    }
}


export class CalendarEntryCreateBatchReq {
    items: CalendarEntryCreateTypedReq[] = [];

    constructor(init?: Partial<CalendarEntryCreateBatchReq>) {
        Object.assign(this, init);
        this.items = init?.items ?? [];
    }
}

export class CalendarDetailsUserRes {
    id!: number;
    title?: string;
    userName!: string;
    lastName!: string;
    givenName!: string;
    email!: string;
    salutationStatus!: Salutation;

    constructor(init?: Partial<CalendarDetailsUserRes>) {
        Object.assign(this, init);
    }

    get fullName() {
        return (this.title !== undefined ? this.title + ' ' : '') + this.givenName + ' ' + this.lastName;
    }
}

export class CalendarMissingEntriesAggregationRes {
    userCount?: number;
    totalExpectedDayCount?: number;
    totalDaysWithEntriesCount?: number;
    totalMissingDaysCount?: number;
}

export class CalendarMissingEntriesUserRes {
    @Type(() => CalendarMissingEntriesUserRes)
    user!: CalendarDetailsUserRes;
    expectedDaysCount?: number;
    daysWithEntriesCount?: number;
    missingDaysCount?: number;
    missingDays: string[] = [];

    get missingDates(){
        return this.missingDays.map(x => new Date(x))
    }
}

export class CalendarMissingEntriesGroupingRes{

}